import React from "react";
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Box,
  Divider,
  Avatar,
  Paper,
  Button,
  useMediaQuery,
  useTheme,
  Fade,
  IconButton,
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import { Link } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import PageTitle from '../components/PageTitle';

// Custom arrow components for slider
const PrevArrow = (props) => {
  const { onClick } = props;
  return (
    <IconButton
      onClick={onClick}
      sx={{
        position: "absolute",
        left: { xs: "-15px", md: "-30px" },
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 2,
        bgcolor: "background.paper",
        color: "primary.main",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        "&:hover": {
          bgcolor: "background.paper",
          color: "primary.dark",
        },
        display: { xs: "none", md: "flex" },
      }}
    >
      <KeyboardArrowLeftIcon />
    </IconButton>
  );
};

const NextArrow = (props) => {
  const { onClick } = props;
  return (
    <IconButton
      onClick={onClick}
      sx={{
        position: "absolute",
        right: { xs: "-15px", md: "-30px" },
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 2,
        bgcolor: "background.paper",
        color: "primary.main",
        boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
        "&:hover": {
          bgcolor: "background.paper",
          color: "primary.dark",
        },
        display: { xs: "none", md: "flex" },
      }}
    >
      <KeyboardArrowRightIcon />
    </IconButton>
  );
};

// Updated team member data with additional director
const teamMembers = [
  
  {
    name: "Priyanka Saxena",
    position: "Director & COO | Operations Expert",
    description: "Priyanka Saxena, a B.Com, M.Com, and LLB graduate, has over 7 years of experience in corporate law. Her expertise in legal compliance and corporate governance plays a pivotal role in ensuring Shoora Welfare's operations are aligned with regulatory standards and best practices.",
    image: "/path-to-image2.jpg",
    specializations: [
      "Operations Management",
      "Strategic Planning",
      "Risk Management",
      "Program Implementation"
    ],
  },{
    name: "Rahul Saxena",
    position: "Founder & CEO | Financial Inclusion Expert",
    description: "Rahul Saxena, a B.Tech graduate, has over 9 years of experience in the banking and finance sector. Having worked in multiple IT sector companies, he brings a unique blend of technical expertise and financial acumen to drive Shoora Welfare's mission of empowering communities through microfinance solutions.",
    image: "/path-to-image1.jpg",
    specializations: [
      "Microfinance Operations",
      "Community Development",
      "Financial Inclusion",
      "Social Impact Programs"
    ],
  },
  
];

// Updated core values for microfinance focus
const coreValues = [
  {
    title: "Financial Inclusion",
    description: "We are committed to making financial services accessible to underserved communities, promoting economic growth and social development at the grassroots level.",
    icon: <AccountBalanceIcon fontSize="large" />,
  },
  {
    title: "Transparency",
    description: "Our operations are built on complete transparency and ethical practices, ensuring trust and accountability in all our financial services.",
    icon: <VerifiedIcon fontSize="large" />,
  },
  {
    title: "Community Impact",
    description: "We measure our success through the positive changes we bring to communities, focusing on sustainable development and economic empowerment.",
    icon: <EmojiEventsIcon fontSize="large" />,
  }
];

// Updated why choose us content for microfinance
const whyChooseUs = [
  {
    title: "Community-Focused Approach",
    description: "Our services are designed with local communities in mind, ensuring financial solutions that truly meet their needs and aspirations.",
  },
  {
    title: "Proven Impact",
    description: "Our track record shows significant improvement in the lives of our beneficiaries through sustainable financial support and guidance.",
  },
  {
    title: "Comprehensive Support",
    description: "Beyond financial services, we provide training, mentorship, and community development programs for holistic growth.",
  }
];

const About = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const teamSliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: isTablet ? 1 : 2,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    arrows: !isMobile,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    responsive: [
      {
        breakpoint: 960,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false,
          dots: true,
        },
      },
    ],
  };

  return (
    <>
      <PageTitle title="About Us" />
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: { xs: 6, sm: 8, md: 10 },
          position: 'relative',
          overflow: 'hidden',
          backgroundImage: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
        }}
      >
        <Container maxWidth="lg">
          <Fade in={true} timeout={1000}>
            <Box
              sx={{
                maxWidth: "800px",
                mx: "auto",
                textAlign: "center",
                position: "relative",
                zIndex: 2,
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  color: 'secondary.light',
                  fontWeight: 600,
                  letterSpacing: 1.5,
                  mb: 2,
                  display: 'block'
                }}
              >
                WELCOME TO SHOORA WELFARE FOUNDATION
              </Typography>
              <Typography
                variant="h2"
                component="h1"
                gutterBottom
                className="font-heading"
                sx={{
                  fontWeight: 900,
                  fontSize: { xs: '2rem', sm: '2.5rem', md: '3.5rem' },
                  letterSpacing: '-0.02em',
                  mb: 2,
                }}
              >
                Empowering Communities Through Financial Inclusion
              </Typography>
              <Typography
                variant="h5"
                className="font-sans"
                sx={{
                  mb: 4,
                  lineHeight: 1.5,
                  fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
                  fontWeight: 400,
                  opacity: 0.9,
                }}
              >
                Since 2000, we've been dedicated to providing accessible financial services
                and fostering economic growth in underserved communities
              </Typography>
              <Button
                component={Link}
                to="/contact"
                variant="contained"
                color="secondary"
                size={isMobile ? "medium" : "large"}
                sx={{
                  fontWeight: 600,
                  px: { xs: 3, md: 4 },
                  py: { xs: 1.2, md: 1.5 },
                  fontSize: { xs: '0.9rem', md: '1rem' },
                  textTransform: 'none',
                  boxShadow: '0 4px 14px 0 rgba(0,0,0,0.2)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 20px 0 rgba(0,0,0,0.3)',
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                Join Our Mission
              </Button>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Mission and Impact Section */}
      <Box sx={{ py: { xs: 5, md: 8 }, bgcolor: 'background.default' }}>
        <Container maxWidth="lg">
          <Grid 
            container 
            spacing={{ xs: 4, md: 6 }} 
            sx={{ mb: { xs: 5, md: 8 } }}
            alignItems="center"
          >
            <Grid item xs={12} md={6}>
              <Fade in={true} timeout={1000}>
                <Box>
                  <Typography 
                    variant="h4" 
                    gutterBottom 
                    sx={{ 
                      fontWeight: 700,
                      fontSize: { xs: '1.75rem', sm: '2rem', md: '2.25rem' },
                      mb: 2,
                      color: 'primary.main'
                    }}
                  >
                    Our Mission and Impact
                  </Typography>
                  <Divider 
                    sx={{ 
                      width: '60px',
                      height: '3px',
                      backgroundColor: 'secondary.main',
                      mb: 3
                    }}
                  />
                  <Typography 
                    paragraph 
                    sx={{ 
                      fontSize: { xs: '1rem', md: '1.1rem' },
                      mb: 3,
                      color: 'text.primary'
                    }}
                  >
                    Shoora Welfare Foundation is committed to breaking the cycle of poverty
                    through sustainable financial solutions. We believe in the power of
                    microfinance to transform lives and build stronger communities.
                  </Typography>
                  <Typography 
                    paragraph 
                    sx={{ 
                      fontSize: { xs: '1rem', md: '1.1rem' },
                      color: 'text.primary'
                    }}
                  >
                    Through our innovative financial products and community-based approach,
                    we've helped thousands of individuals start and grow their businesses,
                    improve their living standards, and create lasting positive change in
                    their communities.
                  </Typography>
                </Box>
              </Fade>
            </Grid>

            <Grid item xs={12} md={6}>
              <Fade in={true} timeout={1000} style={{ transitionDelay: '200ms' }}>
                <Paper
                  elevation={3}
                  sx={{
                    p: { xs: 3, md: 4 },
                    borderRadius: 3,
                    background: 'linear-gradient(135deg, #f5f7fa 0%, #e3e8ef 100%)',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <Box sx={{ mb: 4 }}>
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        color: 'primary.main',
                        mb: 3
                      }}
                    >
                      Our Impact in Numbers
                    </Typography>
                    <Grid container spacing={2}>
                      {[
                        { number: '550+', label: 'Lives Impacted' },
                        { number: '₹1Cr+', label: 'Loans Disbursed' },
                        { number: '100+', label: 'Communities Served' },
                        { number: '95.9%', label: 'Repayment Rate' }
                      ].map((stat, index) => (
                        <Grid item xs={6} key={index}>
                          <Box
                            sx={{
                              p: 2,
                              textAlign: 'center',
                              bgcolor: 'background.paper',
                              borderRadius: 2,
                              boxShadow: '0 2px 8px rgba(0,0,0,0.05)'
                            }}
                          >
                            <Typography
                              variant="h4"
                              sx={{
                                fontWeight: 700,
                                color: 'primary.main',
                                mb: 1,
                                fontSize: { xs: '1.5rem', md: '1.75rem' }
                              }}
                            >
                              {stat.number}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.secondary',
                                fontWeight: 500
                              }}
                            >
                              {stat.label}
                            </Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                </Paper>
              </Fade>
            </Grid>
          </Grid>

          {/* Why Choose Us Section */}
          <Box sx={{ my: { xs: 5, md: 8 }, textAlign: "center" }}>
            <Fade in={true} timeout={1000}>
              <Typography
                variant="h4"
                gutterBottom
                align="center"
                sx={{ 
                  fontWeight: 700, 
                  mb: 2,
                  fontSize: { xs: "1.75rem", sm: "2rem", md: "2.25rem" },
                }}
              >
                Why Choose Us
              </Typography>
            </Fade>
            <Fade in={true} timeout={1000} style={{ transitionDelay: "100ms" }}>
              <Divider 
                sx={{ 
                  width: "60px", 
                  height: "3px", 
                  backgroundColor: "primary.main", 
                  mx: "auto",
                  mb: 4
                }}
              />
            </Fade>
            <Grid container spacing={{ xs: 3, md: 4 }}>
              {whyChooseUs.map((item, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Fade in={true} timeout={1000} style={{ transitionDelay: `${200 + index * 100}ms` }}>
                    <Card
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        boxShadow: 2,
                        borderRadius: 3,
                        overflow: "hidden",
                        transition: "transform 0.3s, box-shadow 0.3s",
                        "&:hover": {
                          transform: "translateY(-5px)",
                          boxShadow: 5,
                        },
                      }}
                    >
                      <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                        <Typography
                          variant="h5"
                          gutterBottom
                          sx={{ 
                            fontWeight: 600, 
                            textAlign: "center",
                            fontSize: { xs: "1.25rem", md: "1.5rem" },
                            color: "primary.dark"
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Divider sx={{ width: "40px", mx: "auto", my: 2 }} />
                        <Typography
                          variant="body1"
                          sx={{ 
                            textAlign: "center", 
                            mt: 2,
                            fontSize: { xs: "0.95rem", md: "1rem" },
                            color: "text.secondary"
                          }}
                        >
                          {item.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Team Member Section */}
          <Box 
            sx={{ 
              my: { xs: 6, md: 10 }, 
              textAlign: "center",
              position: "relative",
              px: { xs: 2, md: 4 },
              pb: 6 // Add padding bottom for dots
            }}
          >
            <Fade in={true} timeout={1000}>
              <Typography
                variant="h3"
                gutterBottom
                align="center"
                sx={{ 
                  fontWeight: 800, 
                  mb: 2,
                  fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                  background: 'linear-gradient(45deg, primary.main, primary.dark)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Our Distinguished Team
              </Typography>
            </Fade>
            <Fade in={true} timeout={1000} style={{ transitionDelay: "100ms" }}>
              <Typography
                variant="h6"
                sx={{
                  mb: 5,
                  color: 'text.secondary',
                  maxWidth: '700px',
                  mx: 'auto',
                  fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' },
                  lineHeight: 1.6
                }}
              >
                Meet our dedicated team of experts committed to empowering communities through financial inclusion
              </Typography>
            </Fade>
            <Box
              sx={{
                '.slick-track': {
                  display: 'flex',
                  '& .slick-slide': {
                    height: 'inherit',
                    '& > div': {
                      height: '100%'
                    }
                  }
                },
                '.slick-slide': {
                  px: { xs: 1.5, md: 2.5 },
                  py: 1,
                },
                '.slick-dots': {
                  bottom: -50,
                  '& li button:before': {
                    fontSize: '10px',
                    color: 'primary.main',
                    opacity: 0.4,
                  },
                  '& li.slick-active button:before': {
                    opacity: 1,
                  },
                },
                '.slick-prev, .slick-next': {
                  width: '40px',
                  height: '40px',
                  backgroundColor: 'background.paper',
                  boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
                  borderRadius: '50%',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    backgroundColor: 'background.paper',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
                  },
                  '&:before': {
                    display: 'none',
                  },
                },
              }}
            >
              <Slider {...teamSliderSettings}>
                {teamMembers.map((member, index) => (
                  <Box key={index} sx={{ height: '100%' }}>
                    <Card
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        borderRadius: 3,
                        overflow: 'hidden',
                        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                        transition: 'all 0.3s ease-in-out',
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
                        },
                        bgcolor: 'background.paper',
                        position: 'relative',
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          right: 0,
                          height: '4px',
                          background: 'linear-gradient(90deg, primary.main, primary.dark)',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: '100%', md: '45%' },
                          bgcolor: 'grey.50',
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          p: { xs: 4, md: 5 },
                          position: 'relative',
                          overflow: 'hidden',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: 'linear-gradient(45deg, rgba(0,0,0,0.02) 0%, rgba(0,0,0,0.06) 100%)',
                            zIndex: 1,
                          },
                        }}
                      >
                        <Avatar
                          sx={{
                            width: { xs: 160, md: 200 },
                            height: { xs: 160, md: 200 },
                            bgcolor: 'primary.main',
                            fontSize: { xs: '3rem', md: '4rem' },
                            fontWeight: 600,
                            border: '6px solid white',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                            zIndex: 2,
                          }}
                        >
                          {member.name.charAt(0)}
                        </Avatar>
                        <Typography
                          variant="h5"
                          sx={{
                            mt: 3,
                            fontWeight: 700,
                            color: 'text.primary',
                            textAlign: 'center',
                            zIndex: 2,
                          }}
                        >
                          {member.name}
                        </Typography>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            color: 'primary.main',
                            fontWeight: 600,
                            textAlign: 'center',
                            zIndex: 2,
                          }}
                        >
                          {member.position}
                        </Typography>
                      </Box>

                      <Box
                        sx={{
                          width: { xs: '100%', md: '55%' },
                          p: { xs: 3, md: 4 },
                          display: 'flex',
                          flexDirection: 'column',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: { xs: '0.95rem', md: '1.1rem' },
                            color: 'text.secondary',
                            lineHeight: 1.8,
                            mb: 3,
                          }}
                        >
                          {member.description}
                        </Typography>

                        <Box sx={{ mt: 'auto' }}>
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontWeight: 700,
                              color: 'primary.main',
                              mb: 2,
                            }}
                          >
                            Areas of Expertise:
                          </Typography>
                          <Grid container spacing={1}>
                            {member.specializations.map((spec, idx) => (
                              <Grid item xs={12} sm={6} key={idx}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    p: 1,
                                    borderRadius: 1,
                                    bgcolor: 'grey.50',
                                    '&:hover': {
                                      bgcolor: 'grey.100',
                                    },
                                    transition: 'background-color 0.3s ease',
                                  }}
                                >
                                  <Box
                                    sx={{
                                      width: 6,
                                      height: 6,
                                      borderRadius: '50%',
                                      bgcolor: 'primary.main',
                                      flexShrink: 0,
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: 'text.primary',
                                      fontSize: '0.9rem',
                                      fontWeight: 500,
                                    }}
                                  >
                                    {spec}
                                  </Typography>
                                </Box>
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                      </Box>
                    </Card>
                  </Box>
                ))}
              </Slider>
            </Box>
          </Box>

          {/* Core Values Section */}
          <Box sx={{ my: { xs: 5, md: 8 } }}>
            <Fade in={true} timeout={1000}>
              <Typography
                variant="h4"
                gutterBottom
                sx={{ 
                  fontWeight: 700, 
                  textAlign: "center", 
                  mb: 2,
                  fontSize: { xs: "1.75rem", sm: "2rem", md: "2.25rem" },
                }}
              >
                Our Core Values
              </Typography>
            </Fade>
            <Fade in={true} timeout={1000} style={{ transitionDelay: "100ms" }}>
              <Divider 
                sx={{ 
                  width: "60px", 
                  height: "3px", 
                  backgroundColor: "primary.main", 
                  mx: "auto",
                  mb: 4
                }}
              />
            </Fade>
            <Grid container spacing={{ xs: 3, md: 4 }}>
              {coreValues.map((value, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Fade in={true} timeout={1000} style={{ transitionDelay: `${200 + index * 100}ms` }}>
                    <Card
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        boxShadow: 2,
                        borderRadius: 3,
                        overflow: "hidden",
                        transition: "transform 0.3s, box-shadow 0.3s",
                        "&:hover": {
                          transform: "translateY(-5px)",
                          boxShadow: 5,
                        },
                      }}
                    >
                      <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                        <Box 
                          sx={{ 
                            display: "flex", 
                            justifyContent: "center", 
                            mb: 2,
                            color: "primary.main" 
                          }}
                        >
                          {React.cloneElement(value.icon, { 
                            sx: { fontSize: "3rem" } 
                          })}
                        </Box>
                        <Typography
                          variant="h5"
                          gutterBottom
                          sx={{ 
                            fontWeight: 600, 
                            textAlign: "center",
                            fontSize: { xs: "1.25rem", md: "1.5rem" },
                            color: "primary.dark"
                          }}
                        >
                          {value.title}
                        </Typography>
                        <Divider sx={{ width: "40px", mx: "auto", my: 2 }} />
                        <Typography
                          variant="body1"
                          sx={{ 
                            textAlign: "center", 
                            mt: 2,
                            fontSize: { xs: "0.95rem", md: "1rem" },
                            color: "text.secondary" 
                          }}
                        >
                          {value.description}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Fade>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Call to Action Section */}
          <Fade in={true} timeout={1000}>
            <Box 
              sx={{ 
                my: { xs: 5, md: 8 }, 
                textAlign: "center",
                p: { xs: 3, md: 5 },
                borderRadius: 3,
                background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                color: "white",
                boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
                position: 'relative',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ position: 'relative', zIndex: 2 }}>
                <Typography 
                  variant="h4" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 800,
                    fontSize: { xs: "1.75rem", md: "2.25rem" },
                    mb: 3,
                  }}
                >
                  Explore Our Financial Products
                </Typography>
                <Typography 
                  variant="body1" 
                  paragraph
                  sx={{ 
                    maxWidth: "800px", 
                    mx: "auto", 
                    mb: 4,
                    fontSize: { xs: "1rem", md: "1.15rem" },
                    opacity: 0.9,
                    lineHeight: 1.7
                  }}
                >
                  Discover our range of tailored financial solutions designed to support your growth. From Income Generating Loans to Joint Liability Group offerings, we provide flexible terms and competitive rates to help you achieve your financial goals.
                </Typography>
                <Grid 
                  container 
                  spacing={3} 
                  sx={{
                    maxWidth: "900px",
                    mx: "auto",
                    mb: 4,
                  }}
                >
                  {[
                    {
                      title: "Income Generating Loans",
                      description: "Loans from ₹10,000 to ₹40,000 with flexible repayment options"
                    },
                    {
                      title: "Joint Liability Groups",
                      description: "Group-based lending fostering community growth and mutual support"
                    },
                    {
                      title: "Financial Advisory",
                      description: "Expert guidance and support throughout your financial journey"
                    }
                  ].map((product, index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Box
                        sx={{
                          bgcolor: 'rgba(255,255,255,0.15)',
                          backdropFilter: 'blur(10px)',
                          p: 3,
                          borderRadius: 2,
                          height: '100%',
                          border: '1px solid rgba(255,255,255,0.2)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-5px)',
                            bgcolor: 'rgba(255,255,255,0.2)',
                            boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
                          }
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            mb: 2,
                            fontSize: { xs: '1.1rem', md: '1.25rem' }
                          }}
                        >
                          {product.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: { xs: '0.9rem', md: '1rem' },
                            opacity: 0.9,
                            lineHeight: 1.6
                          }}
                        >
                          {product.description}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                <Button
                  component={Link}
                  to="/services"
                  variant="contained"
                  color="secondary"
                  size={isMobile ? "medium" : "large"}
                  sx={{
                    fontWeight: 600,
                    px: { xs: 4, md: 6 },
                    py: { xs: 1.5, md: 2 },
                    borderRadius: "8px",
                    textTransform: "none",
                    fontSize: { xs: "0.9rem", md: "1rem" },
                    boxShadow: "0 4px 14px 0 rgba(0, 0, 0, 0.2)",
                    bgcolor: 'secondary.main',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 6px 20px 0 rgba(0, 0, 0, 0.3)',
                      bgcolor: 'secondary.dark',
                    },
                    transition: 'all 0.3s ease'
                  }}
                >
                  View All Products
                </Button>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>
    </>
  );
};

export default About;
